/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2022 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import { FC, useEffect, useState } from 'react'

import {
  commentsLogService,
  useCoordinator,
  CommentItem,
  UUID,
  useGetSettings,
} from '@builddocs/data-management'
import { useToast } from '@builddocs/system'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import {
  IconButton,
  Stack,
  FormControlLabel,
  TextField,
  Checkbox,
  Box,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { Controller, useForm } from 'react-hook-form'

type Props = {
  comment: CommentItem
  forceContractId?: UUID
  onClose: () => void
  refreshComments: () => void
  withoutDeadline?: boolean
}

export const EditComment: FC<Props> = ({
  comment,
  forceContractId,
  onClose,
  refreshComments,
  withoutDeadline,
}) => {
  const { contractId, projectId } = useCoordinator() as {
    contractId: string
    projectId: string
  }

  const { toast } = useToast()

  const {
    data: setting,
    isLoading: isSettingsLoading,
    isError,
  } = useGetSettings({
    projectId,
  })

  const { handleSubmit, errors, control, reset } = useForm({
    defaultValues: {
      resolved: comment.resolved,
      information: comment.information,
      problemDescription: comment.problemDescription,
      deadline: comment?.deadline ?? null,
    },
  })

  useEffect(() => {
    if (!isError && !isSettingsLoading) {
      reset({
        resolved: comment.resolved,
        information: comment.information,
        problemDescription: comment.problemDescription,
        deadline: comment?.deadline ?? null,
      })
    }
  }, [isSettingsLoading])

  const [isLoading, setIsLoading] = useState(false)

  const onUpdate = (data: any) => {
    setIsLoading(true)
    commentsLogService
      .update(forceContractId || contractId, {
        ...data,
        commentId: comment.commentId,
      })
      .then(() => {
        onClose()
        refreshComments()
      })
      .catch(error => {
        toast.error(
          error?.response?.data?.message ?? 'Не удалось обновить запись',
        )
      })
      .finally(() => setIsLoading(false))
  }

  return (
    <form style={{ position: 'relative' }} onSubmit={handleSubmit(onUpdate)}>
      <Stack mt={1} spacing={1}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={2}>
            <Controller
              control={control}
              name="information"
              render={field => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  disabled={isLoading}
                  error={!!errors?.information?.message}
                  helperText={errors?.information?.message}
                  label="Место в документах"
                  size="small"
                />
              )}
              rules={{ required: 'Должно быть заполнено' }}
            />
            <Controller
              control={control}
              name="problemDescription"
              render={field => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  disabled={isLoading}
                  error={!!errors?.problemDescription?.message}
                  helperText={errors?.problemDescription?.message}
                  label="Комментарий"
                  size="small"
                />
              )}
              rules={{ required: 'Должно быть заполнено' }}
            />
          </Stack>
          <Stack direction="row">
            <Controller
              control={control}
              name="deadline"
              render={({ onChange, value, ...rest }) => (
                <DatePicker
                  {...rest}
                  clearable
                  label="Дедлайн"
                  mask="__.__.____"
                  minDate={new Date()}
                  renderInput={params => (
                    <TextField
                      {...params}
                      error={!!errors?.deadline?.message}
                      helperText={errors?.deadline?.message}
                      size="small"
                    />
                  )}
                  value={value}
                  onChange={newValue => {
                    onChange(newValue)
                  }}
                />
              )}
              rules={{
                required: setting?.remarkingDaysMax
                  ? 'Должно быть заполнено'
                  : undefined,
              }}
            />
            <TextField sx={{ display: 'none' }} />
          </Stack>
          {!withoutDeadline && (
            <Box>
              <Controller
                as={
                  <TextField
                    multiline
                    disabled={isLoading}
                    error={!!errors?.deadline?.message}
                    helperText={errors?.deadline?.message}
                    label="Срок устранения"
                    name="deadline"
                    size="small"
                  />
                }
                control={control}
                name="deadline"
                rules={{
                  required: 'Должно быть заполнено',
                }}
              />
            </Box>
          )}
        </Stack>
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
        >
          <Controller
            control={control}
            name="resolved"
            render={({ onChange, onBlur, value, ref, ...rest }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value}
                    inputRef={ref}
                    onBlur={onBlur}
                    onChange={e => onChange(e.target.checked)}
                    {...rest}
                  />
                }
                disabled={isLoading}
                label="Замечание устранено"
                {...rest}
              />
            )}
          />
          <Stack direction="row" spacing={1}>
            <IconButton
              aria-label="Update this position"
              size="small"
              type="submit"
            >
              <CheckRoundedIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="Cancel update"
              size="small"
              onClick={onClose}
            >
              <ClearRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
    </form>
  )
}

export default EditComment
